import type {MapEvent} from '@vis.gl/react-google-maps';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

type IntroStage = 'loading' | 'zoomingOut' | 'ready' | 'started';

// The map opens one zoom step wider and settles on the route as the drawing starts.
const INTRO_ZOOM_STEP = 1;

/**
 * Starts the route drawing once both the map and the viewport are ready, whichever comes last.
 * With the camera intro the map stays hidden while it zooms one step out, then fades in and zooms
 * back to the route as the drawing starts.
 */
export function useMapIntro(hasCameraIntro: boolean, onStart: () => void) {
  const stageRef = useRef<IntroStage>('loading');
  const isInViewRef = useRef(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const fittedZoomRef = useRef<number | null>(null);
  const [isMapHidden, setIsMapHidden] = useState(hasCameraIntro);

  const start = () => {
    if (stageRef.current !== 'ready' || !isInViewRef.current) {
      return;
    }
    stageRef.current = 'started';
    setIsMapHidden(false);
    if (isNotNil(mapRef.current) && isNotNil(fittedZoomRef.current)) {
      mapRef.current.setZoom(fittedZoomRef.current);
    }
    onStart();
  };

  const becomeReady = () => {
    stageRef.current = 'ready';
    start();
  };

  // The fitted zoom is known only once the map has applied the route bounds.
  const zoomOut = (map: google.maps.Map, fittedZoom: number) => {
    fittedZoomRef.current = fittedZoom;
    stageRef.current = 'zoomingOut';
    map.setZoom(fittedZoom - INTRO_ZOOM_STEP);
  };

  const handleIdle = (event: MapEvent) => {
    mapRef.current = event.map;
    const fittedZoom = hasCameraIntro ? event.map.getZoom() : undefined;

    match(stageRef.current)
      .with('loading', () => (isNil(fittedZoom) ? becomeReady() : zoomOut(event.map, fittedZoom)))
      .with('zoomingOut', becomeReady)
      // Later idle events come from the user moving the map.
      .with('ready', 'started', () => undefined)
      .exhaustive();
  };

  const handleViewportEnter = () => {
    isInViewRef.current = true;
    start();
  };

  return {isMapHidden, handleIdle, handleViewportEnter};
}
